import React, { useEffect, useState } from "react";

/**
 * BeamProgress — a subtle horizontal gold hairline that tracks the
 * reader's scroll position on long editorial pages.
 *
 *   - 1px tall, warm gold, scaled from 0→1 along X as the page scrolls
 *   - Fixed to the top of the viewport, offset for the sidebar on
 *     desktop and for the mobile top bar on small screens
 *   - Respects prefers-reduced-motion (hidden entirely)
 *   - Aria-hidden and non-interactive — purely visual, editorial
 *
 * Mount at the top of a long page (Philosophy, About). The line does
 * not exist on short pages so it never becomes a UI habit.
 */
export default function BeamProgress({ testId = "beam-progress" }) {
    const [progress, setProgress] = useState(0);
    const [reduced, setReduced] = useState(false);

    // OS-level reduced motion preference
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => setReduced(mq.matches);
        apply();
        mq.addEventListener?.("change", apply);
        return () => mq.removeEventListener?.("change", apply);
    }, []);

    useEffect(() => {
        let raf = 0;
        const compute = () => {
            const doc = document.documentElement;
            const max = (doc.scrollHeight - doc.clientHeight) || 1;
            const y = window.scrollY || doc.scrollTop || 0;
            setProgress(Math.min(1, Math.max(0, y / max)));
            raf = 0;
        };
        const onScroll = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(compute);
        };
        compute();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, []);

    if (reduced) return null;

    return (
        <div
            aria-hidden="true"
            data-testid={testId}
            className="fixed top-14 lg:top-0 left-0 right-0 lg:left-[var(--sidebar-w)] z-30 pointer-events-none h-px"
        >
            <div
                className="h-px origin-left transition-transform duration-100 ease-out"
                style={{
                    transform: `scaleX(${progress})`,
                    background:
                        "linear-gradient(90deg, rgba(198,161,91,0.85) 0%, rgba(198,161,91,0.55) 60%, rgba(198,161,91,0) 100%)",
                }}
            />
        </div>
    );
}
